import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { MarketingConsent } from "~/MarketingConsent/components/MarketingConsent";
import { IconType } from "~/common/components/icon/IconType";
import type ComponentTypes from "~/CMSComponents/componentTypes";
import escapeRegExp from "lodash/escapeRegExp";
import { userHasMarkettingPermission, userIsPublic } from "~/utils/roles";
import { redirectOrReload } from "~/utils/url/urlUtils";
import { REGISTER_PAGE } from "~/utils/url/staticUrls";
import { sendMarketingConsent } from "~/CMSComponents/api/giveMarketingConsent";
import { isInSmartEdit } from "~/utils/domUtils";
import Modal, { ModalTitle } from "~/common/components/Modal";
import { Button, LoadingButton } from "~/common/components/Button/Button";
import { Box } from "@mui/material";
import { Alert } from "~/common/m3/Alert/Alert";

export interface CMSNewsletterConsentDTO {
  type: ComponentTypes.CMSNewsLetterConsent;
  className?: string;
  title: string;
  subtitle?: string;
  reasons?: {
    icon: string;
    label: string;
  }[];
}

type Props = Omit<CMSNewsletterConsentDTO, "type">;

export const CMSNewsletterConsent = ({ title, subtitle, reasons }: Props) => {
  const { t } = useTranslation();
  const [consent, setConsent] = useState(false);
  const [inFlight, setInFlight] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [error, setError] = useState("");
  const { titleProps, subtitleProps, reasonProps } = useMemo(() => {
    return {
      titleProps: transformText(title),
      subtitleProps: transformText(subtitle),
      reasonProps: reasons?.map(transformReasonDTO),
    };
  }, [title, subtitle, reasons]);

  // If the user already has a newsletter-xx group then the component should not render
  if (userHasMarkettingPermission() && !isInSmartEdit()) {
    return null;
  }

  const handleSelected = async () => {
    if (isInSmartEdit()) {
      return;
    }
    if (userIsPublic()) {
      redirectOrReload(REGISTER_PAGE);
      return;
    }
    try {
      setInFlight(true);
      await sendMarketingConsent(consent);
      setSuccessModal(true);
    } catch (e) {
      console.error(e);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setError(e as any);
    } finally {
      setInFlight(false);
    }
  };

  const onClosePopup = () => window.location.reload();

  return (
    <>
      <MarketingConsent.Card>
        {titleProps && <MarketingConsent.Title {...titleProps} />}
        <MarketingConsent.ConsentCheckbox selected={consent} setSelected={setConsent} disabled={inFlight} />
        <LoadingButton size="medium" onClick={handleSelected} sx={{ mt: 1 }} inFlight={inFlight} disabled={!consent}>
          {t("page.completeRegistration.submit")}
        </LoadingButton>
        {subtitleProps && <MarketingConsent.Title sx={{ mt: 4 }} {...subtitleProps} />}
        {error && (
          <Alert className={"my-2"} severity={"error"}>
            {t(error)}
          </Alert>
        )}
        {reasonProps && reasonProps.length > 0 && <MarketingConsent.Reasons reasons={reasonProps} />}
      </MarketingConsent.Card>
      {successModal && (
        <Modal onClose={onClosePopup} footerChildren={<Button onClick={onClosePopup}>{t("close")}</Button>}>
          <Box sx={{ mb: 4 }}>
            <ModalTitle>{t("cms.newsletterConsent.thankYou")}</ModalTitle>
          </Box>
        </Modal>
      )}
    </>
  );
};

const FUNCTION_PREFIX = '#{t("';
const FUNCTION_SUFFIX = '")}';
const FUNCTION_REGEXP = new RegExp(`^${escapeRegExp(FUNCTION_PREFIX)}(.*)${escapeRegExp(FUNCTION_SUFFIX)}$`);

function transformText(text?: string) {
  if (!text) {
    return null;
  }
  text = text.trim();
  // If matches function regexp, extract the key
  const match = text.match(FUNCTION_REGEXP);
  if (match) {
    return {
      children: <Trans i18nKey={match[1]} />,
    };
  }

  return {
    dangerouslySetInnerHTML: { __html: text },
  };
}

function transformReasonDTO(reason: { icon: string; label: string }) {
  const data = transformText(reason.label);
  const icon = IconType[reason.icon as keyof typeof IconType] ?? IconType.Close;
  return {
    icon,
    children: data?.dangerouslySetInnerHTML ? <div {...data} /> : data?.children,
  };
}
